import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Industry } from '../types';

interface Theme {
  mode: 'light' | 'dark';
  industry?: Industry;
  colors: {
    primary: string;
    secondary: string;
    success: string;
    error: string;
    warning: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
  };
}

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setIndustryTheme: (industry: Industry) => void;
  resetTheme: () => void;
}

const defaultTheme: Theme = {
  mode: 'light',
  colors: {
    primary: '#3B82F6',
    secondary: '#6B7280',
    success: '#10B981',
    error: '#EF4444',
    warning: '#F59E0B',
    background: '#FFFFFF',
    surface: '#F9FAFB',
    text: '#1F2937',
    textSecondary: '#6B7280',
  },
};

const industryThemes: { [key: string]: Partial<Theme['colors']> } = {
  tourism: {
    primary: '#3B82F6',
    secondary: '#60A5FA',
    background: '#EFF6FF',
    surface: '#F0F9FF',
  },
  isp: {
    primary: '#10B981',
    secondary: '#34D399',
    background: '#ECFDF5',
    surface: '#F0FDF4',
  },
  medical: {
    primary: '#EF4444',
    secondary: '#F87171',
    background: '#FEF2F2',
    surface: '#FEF7F7',
  },
  legal: {
    primary: '#8B5CF6',
    secondary: '#A78BFA',
    background: '#F5F3FF',
    surface: '#FAF5FF',
  },
  manufacturing: {
    primary: '#F59E0B',
    secondary: '#FBBF24',
    background: '#FFFBEB',
    surface: '#FFFEFF',
  }
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(defaultTheme);

  const toggleTheme = () => {
    setTheme(prev => ({
      ...prev,
      mode: prev.mode === 'light' ? 'dark' : 'light'
    }));
  };

  const setIndustryTheme = (industry: Industry) => {
    const industryTheme = industryThemes[industry.id] || {};
    setTheme(prev => ({
      ...prev,
      industry,
      colors: {
        ...prev.colors,
        ...industryTheme,
        primary: industry.themeColor || industryTheme.primary || prev.colors.primary
      }
    }));
  };

  const resetTheme = () => {
    setTheme(defaultTheme);
  };

  // Apply CSS custom properties when theme changes
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setIndustryTheme, resetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
